<template>
  <div>

    <div class="container">
      <div class="box-title">
        <h4>
          <i class="fal fa-wallet"></i>
          ຈັດການກະເປົ໋າສາຂາ
          <span class="countTotal">({{ totalBranches }})</span>
        </h4>
        <div class="title-actions">
          <span v-if="lastUpdated" class="last-updated">
            <i class="fal fa-clock"></i> ອັບເດດລ່າສຸດ: {{ formatLastUpdated }}
          </span>
          <!-- Branch-wallet MIGRATION (closing branch → successor, whole signed position). Page-level,
               not per-row: the closing branch's wallet flag is already off (runbook step 0), which
               hides the per-row actions. Same role set as the transfer button (must match the
               can_transfer mint in Laravel AdminWalletController) — UI-only gate, Go enforces the
               `can_transfer` claim and 404s while WALLET_MIGRATION_ENABLED is off. -->
          <button
            v-if="$can(['SuperAdmin', 'accountingmanager', 'accountingoriginfranchise'])"
            type="button"
            class="button is-small migrate-open-btn"
            :disabled="!walletServiceAvailable"
            :title="walletServiceAvailable ? 'ຍ້າຍກະເປົ໋າສາຂາ (ປິດສາຂາ)' : 'ລະບົບກະເປົ໋າບໍ່ສາມາດເຊື່ອມຕໍ່ໄດ້'"
            @click="showMigrateDialog = true"
          >
            <i class="fal fa-people-carry"></i>&nbsp;ຍ້າຍກະເປົ໋າສາຂາ
          </button>
          <!-- WALLETS export (full dump; blocked filter selected → blocked set
               with ຕ້ອງເຕີມ — see exportWallets). -->
          <vs-button
            flat
            size="small"
            @click="exportWallets"
            :loading="isExporting"
            :disabled="!walletServiceAvailable"
            :title="walletServiceAvailable ? 'Export Excel' : 'ລະບົບກະເປົ໋າບໍ່ສາມາດເຊື່ອມຕໍ່ໄດ້'"
          >
            <i class="fal fa-file-export"></i> Export Excel
          </vs-button>
          <vs-button flat size="small" @click="refreshData" :loading="isLoading" title="ໂຫລດຂໍ້ມູນໃໝ່">
            <i class="fal fa-sync-alt"></i>
          </vs-button>
        </div>
      </div>

      <!-- Wallet Service Down Banner -->
      <div v-if="!walletServiceAvailable" class="box">
        <div class="notification is-warning">
          <p>ບໍ່ສາມາດເຊື່ອມຕໍ່ລະບົບກະເປົ໋າໄດ້ — ຂໍ້ມູນຍອດເງິນອາດບໍ່ອັບເດດ, ບໍ່ສາມາດເປີດ/ປິດກະເປົ໋າໄດ້</p>
          <vs-button @click="fetchBranches" class="mt-3">
            <i class="fal fa-redo"></i> ລອງໃໝ່
          </vs-button>
        </div>
      </div>

      <!-- Health filter could not be applied (Go down / matching set too large) -->
      <div v-if="healthFilterError" class="box">
        <div class="notification is-warning">
          <p>ບໍ່ສາມາດກັ່ນຕອງຕາມສະຖານະຍອດເງິນໄດ້ໃນຂະນະນີ້ — ຜົນລັບອາດບໍ່ຄົບຖ້ວນ. ກະລຸນາລອງໃໝ່.</p>
          <vs-button @click="fetchBranches" class="mt-3">
            <i class="fal fa-redo"></i> ລອງໃໝ່
          </vs-button>
        </div>
      </div>

      <!-- Filters -->
      <div class="box">
        <div class="columns is-multiline">
          <div class="column is-3">
            <div class="control">
              <label class="label">ຄົ້ນຫາ</label>
              <input
                class="input"
                placeholder="ຊື່ສາຂາ, ລະຫັດ..."
                v-model="searchQuery"
                @keyup.enter="fetchBranches"
              >
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ສະຖານະກະເປົ໋າ</label>
              <div class="select is-fullwidth">
                <select v-model="walletFilter" @change="onFilterChange">
                  <option value="all">ທັງໝົດ</option>
                  <option value="enabled">ເປີດໃຊ້ງານ</option>
                  <option value="disabled">ປິດໃຊ້ງານ</option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ສະຖານະຍອດເງິນ</label>
              <div class="select is-fullwidth">
                <select v-model="balanceFilter" @change="onFilterChange">
                  <option value="all">ທັງໝົດ</option>
                  <option value="negative">ຕິດລົບ</option>
                  <option value="low">ຍອດຕ່ຳ (ເຕືອນ)</option>
                  <option value="blocked">ຖືກລັອກ (ຍອດຄ້າງມື້ກ່ອນ)</option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ປະເພດກະເປົ໋າ</label>
              <div class="select is-fullwidth">
                <select v-model="walletTypeFilter" @change="onFilterChange" :disabled="balanceFilter === 'all'">
                  <option value="">ທັງໝົດ</option>
                  <option value="FREIGHT">FREIGHT (ຄ່າຂົນສົ່ງ)</option>
                  <option value="COD">COD</option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-2">
            <div class="control">
              <label class="label">ປະເພດສາຂາ</label>
              <div class="select is-fullwidth">
                <select v-model="typeFilter" @change="onFilterChange">
                  <option value="">ທັງໝົດ</option>
                  <option value="branch">ສາຂາທົ່ວໄປ</option>
                  <option value="franchise">ສາຂາແຟນຊາຍ</option>
                  <option value="delivery">ສາຂາດີລີເວີລີ່</option>
                </select>
              </div>
            </div>
          </div>
          <div class="column is-3">
            <div class="control">
              <label class="label">&nbsp;</label>
              <vs-button @click="fetchBranches" :loading="isLoading">
                <i class="fal fa-search"></i> ຄົ້ນຫາ
              </vs-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Batch Actions -->
      <!-- Batch enable/disable: same set as the per-row toggle below — keep the two in sync, a role
           that can toggle one branch must be able to toggle a selection. UI-only gate, not enforced
           at Go. ⚠️ This bar can disable up to 1000 branches in one call. -->
      <div v-if="selectedBranches.length > 0 && $can(['SuperAdmin', 'accountingmanager', 'accountingoriginfranchise', 'accountingcod'])" class="batch-actions box">
        <span class="selected-count">ເລືອກແລ້ວ {{ selectedBranches.length }} ສາຂາ</span>
        <vs-button v-if="isAllSelected && !selectAllPages && totalBranches > branches.length"
          flat size="small" @click="selectAllAcrossPages">
          ເລືອກທັງໝົດ {{ totalBranches }} ສາຂາ
        </vs-button>
        <span v-if="selectAllPages" class="selected-count" style="color: #7367f0; font-weight: 600;">
          (ເລືອກທັງໝົດ {{ totalBranches }} ສາຂາ)
        </span>
        <div class="batch-buttons">
          <vs-button flat size="small" @click="batchEnableWallets" :loading="isBatchProcessing" :disabled="!walletServiceAvailable">
            <i class="fal fa-toggle-on"></i> ເປີດກະເປົ໋າ
          </vs-button>
          <vs-button flat size="small" color="danger" @click="batchDisableWallets" :loading="isBatchProcessing" :disabled="!walletServiceAvailable">
            <i class="fal fa-toggle-off"></i> ປິດກະເປົ໋າ
          </vs-button>
          <vs-button flat size="small" @click="clearSelection">
            <i class="fal fa-times"></i> ຍົກເລີກ
          </vs-button>
        </div>
      </div>

      <!-- Blocked-set finance summary (blocked filter only; hidden when the backend
           did not attach health_summary). Totals are scoped to the blocked set. -->
      <div v-if="healthSummary" class="blocked-summary box">
        <div class="blocked-summary-head">
          <i class="fal fa-lock"></i>
          ສາຂາຖືກລັອກ: <strong>{{ healthSummary.blocked_branches }}</strong>
        </div>
        <div class="blocked-summary-types">
          <div v-if="healthSummary.freight && healthSummary.freight.blocked_wallets > 0" class="blocked-summary-type">
            <span class="type-label">ຄ່າຂົນສົ່ງ</span>
            <span class="type-metric">ຕ້ອງເຕີມ <strong>{{ formatCurrency(healthSummary.freight.total_amount_to_unblock) }}</strong></span>
            <span class="type-metric">ຍອດເຫຼືອ <strong>{{ formatCurrency(healthSummary.freight.total_balance) }}</strong></span>
            <span class="type-count">({{ healthSummary.freight.blocked_wallets }})</span>
          </div>
          <div v-if="healthSummary.cod && healthSummary.cod.blocked_wallets > 0" class="blocked-summary-type">
            <span class="type-label">COD</span>
            <span class="type-metric">ຕ້ອງເຕີມ <strong>{{ formatCurrency(healthSummary.cod.total_amount_to_unblock) }}</strong></span>
            <span class="type-metric">ຍອດເຫຼືອ <strong>{{ formatCurrency(healthSummary.cod.total_balance) }}</strong></span>
            <span class="type-count">({{ healthSummary.cod.blocked_wallets }})</span>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="center">
        <vs-table>
          <template #thead>
            <vs-tr>
              <vs-th style="width: 40px">
                <input
                  v-if="$can(['SuperAdmin', 'accountingmanager', 'accountingoriginfranchise', 'accountingcod'])"
                  type="checkbox"
                  :checked="isAllSelected"
                  :indeterminate.prop="isPartiallySelected"
                  @change="toggleSelectAll"
                  class="batch-checkbox"
                >
              </vs-th>
              <vs-th style="width: 60px">ລຳດັບ</vs-th>
              <vs-th class="sortable" @click="setSort('prefix')">
                ລະຫັດ
                <i :class="getSortIcon('prefix')"></i>
              </vs-th>
              <vs-th class="sortable" @click="setSort('name')">
                ຊື່ສາຂາ
                <i :class="getSortIcon('name')"></i>
              </vs-th>
              <vs-th>ປະເພດ</vs-th>
              <vs-th>ສະຖານະກະເປົ໋າ</vs-th>
              <vs-th class="sortable" @click="setSort('freight_balance')">
                ຍອດຄ່າຂົນສົ່ງ
                <i :class="getSortIcon('freight_balance')"></i>
              </vs-th>
              <vs-th>ຂີດຈຳກັດຄ່າຂົນສົ່ງ</vs-th>
              <vs-th class="sortable" @click="setSort('cod_balance')">
                ຍອດ COD
                <i :class="getSortIcon('cod_balance')"></i>
              </vs-th>
              <vs-th>ຂີດຈຳກັດ COD</vs-th>
              <vs-th></vs-th>
            </vs-tr>
          </template>
          <template #tbody>
            <!-- Loading Skeletons -->
            <template v-if="isLoading && branches.length === 0">
              <vs-tr v-for="n in 5" :key="'skeleton-' + n">
                <vs-td><div class="skeleton skeleton-checkbox"></div></vs-td>
                <vs-td><div class="skeleton skeleton-text-sm"></div></vs-td>
                <vs-td><div class="skeleton skeleton-text-sm"></div></vs-td>
                <vs-td><div class="skeleton skeleton-text"></div></vs-td>
                <vs-td><div class="skeleton skeleton-text-sm"></div></vs-td>
                <vs-td><div class="skeleton skeleton-toggle"></div></vs-td>
                <vs-td><div class="skeleton skeleton-text"></div></vs-td>
                <vs-td><div class="skeleton skeleton-text-sm"></div></vs-td>
                <vs-td><div class="skeleton skeleton-text"></div></vs-td>
                <vs-td><div class="skeleton skeleton-text-sm"></div></vs-td>
                <vs-td><div class="skeleton skeleton-actions"></div></vs-td>
              </vs-tr>
            </template>
            <!-- Data Rows -->
            <vs-tr v-else v-for="(branch, index) in sortedBranches" :key="branch.id" :class="{ 'selected-row': isSelected(branch.id) }">
              <vs-td>
                <input
                  v-if="$can(['SuperAdmin', 'accountingmanager', 'accountingoriginfranchise', 'accountingcod'])"
                  type="checkbox"
                  :checked="isSelected(branch.id)"
                  @change="toggleSelect(branch.id)"
                  class="batch-checkbox"
                >
              </vs-td>
              <vs-td style="text-align: center">{{ getRowNumber(index) }}</vs-td>
              <vs-td style="color: crimson">{{ branch.prefix }}</vs-td>
              <vs-td>
                {{ branch.name }}
                <!-- CLOSED branch (branches.status = 0). Closing a branch does NOT clear
                     wallet_enabled, so a closed branch still shows a live balance and live
                     top-up/transfer buttons — it must be visually distinct. `status` ships as an
                     INT (0/1), unlike the boolean wallet_enabled, so compare with Number(); an
                     absent field (older backend) yields NaN and correctly renders nothing. -->
                <span v-if="Number(branch.status) === 0" class="closed-branch-badge" title="ສາຂານີ້ປິດແລ້ວ">
                  ສາຂາປິດ
                </span>
              </vs-td>
              <vs-td>{{ formatBranchType(branch.type) }}</vs-td>
              <vs-td>
                <!-- Enable/disable wallet: SuperAdmin/accountingmanager/accountingoriginfranchise/
                     accountingcod. NOTE this is deliberately NOT the transfer-role set any more —
                     accountingcod holds the toggle WITHOUT top-up or transfer below, so do not
                     "restore parity" by copying this list to those gates.
                     UI-only gate: the Go 'admin' JWT already authorizes toggle for every accounting
                     role on this screen (not enforced at Go), so this reveals, it does not grant. -->
                <label v-if="$can(['SuperAdmin', 'accountingmanager', 'accountingoriginfranchise', 'accountingcod'])" class="switch-toggle">
                  <input
                      type="checkbox"
                      :checked="branch.wallet_enabled"
                      @change="confirmToggleWallet(branch, $event)"
                      :disabled="togglingBranchId === branch.id || !walletServiceAvailable"
                  >
                  <span class="slider"></span>
                  <span class="toggle-label" :class="branch.wallet_enabled ? 'active' : 'inactive'">
                    {{ branch.wallet_enabled ? 'ເປີດ' : 'ປິດ' }}
                  </span>
                </label>
                <span v-else class="toggle-label" :class="branch.wallet_enabled ? 'active' : 'inactive'">
                  {{ branch.wallet_enabled ? 'ເປີດ' : 'ປິດ' }}
                </span>
              </vs-td>
              <!-- FREIGHT Balance -->
              <vs-td>
                <div v-if="branch.wallets && branch.wallets.freight_wallet" class="balance-cell">
                  <span :class="['balance-text', getBalanceClass(branch.wallets.freight_wallet)]">
                    {{ formatCurrency(branch.wallets.freight_wallet.balance) }}
                  </span>
                  <!-- Freight deadline lock (policy branches) — reuses the COD lock chip styling. -->
                  <span v-if="branch.wallets.freight_wallet.freight_deadline_blocked" class="cod-blocked-badge" title="ຄ່າຂົນສົ່ງຖືກລັອກ — ຍອດຕິດລົບຈາກມື້ກ່ອນ">
                    <i class="fal fa-lock"></i> ລັອກ
                  </span>
                  <i v-else-if="isBalanceLow(branch.wallets.freight_wallet)" class="fal fa-exclamation-triangle warning-icon" title="ຍອດເງິນຕ່ຳກວ່າຂີດຈຳກັດ"></i>
                  <!-- Amount the branch must top up to unblock (render-only, from the row payload). -->
                  <span v-if="branch.wallets.freight_wallet.freight_amount_to_unblock > 0" class="unblock-amount" title="ຕ້ອງເຕີມເພື່ອປົດລັອກ">
                    ຕ້ອງເຕີມ {{ formatCurrency(branch.wallets.freight_wallet.freight_amount_to_unblock) }}
                  </span>
                </div>
                <span v-else-if="branch.wallet_enabled && !walletServiceAvailable" class="text-muted">ບໍ່ສາມາດໂຫລດ</span>
                <div v-else-if="branch.wallet_enabled" class="skeleton skeleton-text"></div>
                <span v-else class="text-muted">-</span>
              </vs-td>
              <!-- FREIGHT Limit -->
              <vs-td>
                <div v-if="branch.wallets && branch.wallets.freight_wallet" class="limit-info">
                  <span class="limit-text" :class="hasNegativeLimit(branch.wallets.freight_wallet) ? 'negative' : 'not-set'">
                    {{ formatLimit(branch.wallets.freight_wallet.minimum_negative_limit) }}
                  </span>
                  <!-- Edit negative limit: SuperAdmin/accountingmanager only. UI-only gate — not enforced at Go. -->
                  <vs-button
                      v-if="$can(['SuperAdmin', 'accountingmanager'])"
                      flat
                      icon
                      size="small"
                      @click="openLimitDialog(branch, 'FREIGHT')"
                      title="ແກ້ໄຂຂີດຈຳກັດ"
                  >
                    <i class="fal fa-edit" style="color: #007bff"></i>
                  </vs-button>
                </div>
                <span v-else class="text-muted">-</span>
              </vs-td>
              <!-- COD Balance -->
              <vs-td>
                <div v-if="branch.wallets && branch.wallets.cod_wallet" class="balance-cell">
                  <span :class="['balance-text', getBalanceClass(branch.wallets.cod_wallet)]">
                    {{ formatCurrency(branch.wallets.cod_wallet.balance) }}
                  </span>
                  <span v-if="branch.wallets.cod_wallet.cod_deadline_blocked" class="cod-blocked-badge" title="COD ຖືກລັອກ — ຍອດຕິດລົບຈາກມື້ກ່ອນ">
                    <i class="fal fa-lock"></i> ລັອກ
                  </span>
                  <i v-else-if="isBalanceLow(branch.wallets.cod_wallet)" class="fal fa-exclamation-triangle warning-icon" title="ຍອດເງິນຕ່ຳກວ່າຂີດຈຳກັດ"></i>
                  <!-- Amount the branch must top up to unblock (render-only, from the row payload). -->
                  <span v-if="branch.wallets.cod_wallet.cod_amount_to_unblock > 0" class="unblock-amount" title="ຕ້ອງເຕີມເພື່ອປົດລັອກ">
                    ຕ້ອງເຕີມ {{ formatCurrency(branch.wallets.cod_wallet.cod_amount_to_unblock) }}
                  </span>
                </div>
                <span v-else-if="branch.wallet_enabled && !walletServiceAvailable" class="text-muted">ບໍ່ສາມາດໂຫລດ</span>
                <div v-else-if="branch.wallet_enabled" class="skeleton skeleton-text"></div>
                <span v-else class="text-muted">-</span>
              </vs-td>
              <!-- COD Limit -->
              <vs-td>
                <div v-if="branch.wallets && branch.wallets.cod_wallet" class="limit-info">
                  <span class="limit-text" :class="hasNegativeLimit(branch.wallets.cod_wallet) ? 'negative' : 'not-set'">
                    {{ formatLimit(branch.wallets.cod_wallet.minimum_negative_limit) }}
                  </span>
                  <!-- Edit negative limit: SuperAdmin/accountingmanager only. UI-only gate — not enforced at Go. -->
                  <vs-button
                      v-if="$can(['SuperAdmin', 'accountingmanager'])"
                      flat
                      icon
                      size="small"
                      @click="openLimitDialog(branch, 'COD')"
                      title="ແກ້ໄຂຂີດຈຳກັດ"
                  >
                    <i class="fal fa-edit" style="color: #007bff"></i>
                  </vs-button>
                </div>
                <span v-else class="text-muted">-</span>
              </vs-td>
              <!-- Actions -->
              <vs-td>
                <!-- Manual top-up: SuperAdmin/accountingmanager/accountingoriginfranchise. UI-only gate —
                     the Go 'admin' JWT still authorizes top-up for the view-only accounting roles (not enforced at Go). -->
                <vs-button
                    v-if="branch.wallet_enabled && $can(['SuperAdmin', 'accountingmanager', 'accountingoriginfranchise'])"
                    flat
                    icon
                    @click="openTopUpDialog(branch)"
                    :disabled="!walletServiceAvailable"
                    :title="walletServiceAvailable ? 'ເຕີມເງິນ' : 'ລະບົບກະເປົ໋າບໍ່ສາມາດເຊື່ອມຕໍ່ໄດ້'"
                >
                  <i class="fal fa-plus-circle" :style="{ color: walletServiceAvailable ? '#28a745' : '#ccc' }"></i>
                </vs-button>
                <!-- Wallet-type transfer: SuperAdmin/accountingmanager/accountingoriginfranchise (must match
                     the can_transfer mint in Laravel AdminWalletController). UI-only gate — Go enforces the
                     real authorization via the `can_transfer` JWT claim. -->
                <vs-button
                    v-if="branch.wallet_enabled && $can(['SuperAdmin', 'accountingmanager', 'accountingoriginfranchise'])"
                    flat
                    icon
                    @click="openTransferDialog(branch)"
                    :disabled="!walletServiceAvailable"
                    :title="walletServiceAvailable ? 'ໂອນເງິນ' : 'ລະບົບກະເປົ໋າບໍ່ສາມາດເຊື່ອມຕໍ່ໄດ້'"
                >
                  <i class="fal fa-exchange" :style="{ color: walletServiceAvailable ? '#007bff' : '#ccc' }"></i>
                </vs-button>
                <vs-button
                    v-if="branch.wallet_enabled"
                    flat
                    icon
                    @click="openTransactionHistory(branch)"
                    :disabled="!walletServiceAvailable"
                    :title="walletServiceAvailable ? 'ປະຫວັດ' : 'ລະບົບກະເປົ໋າບໍ່ສາມາດເຊື່ອມຕໍ່ໄດ້'"
                >
                  <i class="fal fa-history" :style="{ color: walletServiceAvailable ? '#17a2b8' : '#ccc' }"></i>
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
          <template #notFound>
            <div class="not-found">ບໍ່ພົບຂໍ້ມູນ</div>
          </template>
        </vs-table>

        <!-- Pagination -->
        <vs-pagination
            v-if="totalPages > 1"
            v-model="currentPage"
            :length="totalPages"
            @input="fetchBranches"
        />
      </div>
    </div>

    <!-- Top-up Dialog -->
    <TopUpDialog
        v-if="showTopUpDialog"
        :branch="selectedBranch"
        @close="showTopUpDialog = false"
        @success="onTopUpSuccess"
    />

    <!-- Wallet-type Transfer Dialog -->
    <TransferDialog
        v-if="showTransferDialog"
        :branch="selectedBranch"
        @close="showTransferDialog = false"
        @success="onTransferSuccess"
    />

    <!-- Branch-wallet Migration Dialog (source + destination pickers live inside) -->
    <MigrateDialog
        v-if="showMigrateDialog"
        @close="showMigrateDialog = false"
        @success="onMigrateSuccess"
    />

    <!-- Transaction History Dialog -->
    <TransactionHistoryDialog
        v-if="showTransactionHistory"
        :branch="selectedBranch"
        @close="showTransactionHistory = false"
    />

    <!-- Edit Limits Dialog -->
    <vs-dialog v-model="showLimitDialog" prevent-close>
      <template #header>
        <h4 class="dialog-title">
          <i class="fal fa-sliders-h"></i>
          ແກ້ໄຂຂີດຈຳກັດກະເປົ໋າ {{ selectedWalletType }}
        </h4>
      </template>

      <div class="limit-dialog-content" v-if="selectedBranch">
        <div class="info-row">
          <span class="info-label">ສາຂາ:</span>
          <span class="info-value">{{ selectedBranch.name }} ({{ selectedBranch.prefix }})</span>
        </div>
        <div class="info-row">
          <span class="info-label">ປະເພດກະເປົ໋າ:</span>
          <span class="info-value">{{ selectedWalletType }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">ຍອດເງິນປັດຈຸບັນ:</span>
          <span class="info-value balance-text">{{ formatCurrency(getCurrentBalance()) }}</span>
        </div>

        <hr>

        <div class="form-group">
          <label class="label">ວົງເງິນໜີ້ສູງສຸດ</label>
          <div class="control has-icons-left">
            <input
                class="input"
                type="text"
                :value="formatNumberInput(Math.abs(limitForm.minimum_negative_limit))"
                @input="limitForm.minimum_negative_limit = -Math.abs(parsePositiveNumber($event.target.value))"
                @blur="$event.target.value = formatNumberInput(Math.abs(limitForm.minimum_negative_limit))"
                placeholder="0"
            >
            <span class="icon is-small is-left">
              <i class="fal fa-minus-circle"></i>
            </span>
          </div>
          <p class="help">ສາຂາສາມາດຕິດໜີ້ໄດ້ສູງສຸດເທົ່າໃດ (ຕົວຢ່າງ: 500,000 = ຕິດໜີ້ໄດ້ 500,000 ກີບ, ຕັ້ງ 0 ເພື່ອບໍ່ອະນຸຍາດໜີ້)</p>
        </div>

        <div class="form-group">
          <label class="label">ຍອດເງິນຂັ້ນຕ່ຳ (ເຕືອນ)</label>
          <div class="control has-icons-left">
            <input
                class="input"
                type="text"
                :value="formatNumberInput(limitForm.minimum_balance)"
                @input="limitForm.minimum_balance = parseNumberInput($event.target.value)"
                @blur="$event.target.value = formatNumberInput(limitForm.minimum_balance)"
                placeholder="0"
            >
            <span class="icon is-small is-left">
              <i class="fal fa-exclamation-triangle"></i>
            </span>
          </div>
          <p class="help">ຈະແຈ້ງເຕືອນເມື່ອຍອດເງິນຕ່ຳກວ່ານີ້ (ຕັ້ງ 0 ເພື່ອປິດການເຕືອນ)</p>
        </div>

        <div class="available-balance-preview">
          <span class="preview-label">ຍອດທີ່ສາມາດໃຊ້ໄດ້:</span>
          <span class="preview-value">{{ formatCurrency(calculateAvailableBalance()) }}</span>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <vs-button @click="showLimitDialog = false" transparent>
            ຍົກເລີກ
          </vs-button>
          <vs-button @click="saveLimits" :loading="isSavingLimits" primary>
            <i class="fal fa-save"></i> ບັນທຶກ
          </vs-button>
        </div>
      </template>
    </vs-dialog>

    <!-- Confirmation Dialog -->
    <vs-dialog v-model="showConfirmDialog" width="400px" prevent-close>
      <template #header>
        <h4 class="dialog-title">
          <i class="fal fa-question-circle"></i>
          ຢືນຢັນການດຳເນີນການ
        </h4>
      </template>
      <div class="confirm-content">
        <p>{{ confirmMessage }}</p>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <vs-button @click="showConfirmDialog = false" transparent>
            ຍົກເລີກ
          </vs-button>
          <vs-button @click="executeConfirmedAction" :color="confirmDanger ? 'danger' : 'primary'">
            <i class="fal fa-check"></i> ຢືນຢັນ
          </vs-button>
        </div>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import TopUpDialog from './Dialog/TopUp.vue';
import TransferDialog from './Dialog/Transfer.vue';
import MigrateDialog from './Dialog/Migrate.vue';
import TransactionHistoryDialog from './Dialog/TransactionHistory.vue';
import walletService from '@/services/walletService';
import { formatCurrency, formatErrorMessage, formatNumberInput, parseNumberInput } from '@/services/walletFormatters';
import { DEFAULT_PAGE_SIZE, POLLING_INTERVAL, REQUEST_TIMEOUT } from '@/services/walletConstants';

// Export polling bound: 60 polls × POLLING_INTERVAL (2s) ≈ 2 minutes, then give up
// with a warning — the wallet dump is a few hundred rows and normally completes in ~1s.
const MAX_EXPORT_POLLS = 60;

export default {
  name: 'ListBranchWallets',
  components: {
    TopUpDialog,
    TransferDialog,
    MigrateDialog,
    TransactionHistoryDialog,
  },
  data() {
    return {
      branches: [],
      totalBranches: 0,
      totalPages: 1,
      currentPage: 1,
      perPage: DEFAULT_PAGE_SIZE,
      isLoading: false,
      searchQuery: '',
      walletFilter: 'enabled',
      balanceFilter: 'all',
      walletTypeFilter: '',
      typeFilter: '',
      healthFilterError: false,
      // Finance summary of the blocked set (blocked filter only). Non-null only
      // when Laravel attaches health_summary (Go served blocked-summary); older
      // backends / other filters leave it null and the strip stays hidden.
      healthSummary: null,
      allBranches: [],
      showTopUpDialog: false,
      showTransferDialog: false,
      showMigrateDialog: false,
      showTransactionHistory: false,
      showLimitDialog: false,
      selectedBranch: null,
      selectedWalletType: '',
      selectedWallet: null,
      isSavingLimits: false,
      togglingBranchId: null,
      limitForm: {
        minimum_balance: 0,
        minimum_negative_limit: 0,
      },
      branchTypes: {
        branch: 'ສາຂາທົ່ວໄປ',
        franchise: 'ແຟນຊາຍ',
        delivery: 'ດີລີເວີລີ່',
        local_branch: 'ຈຸດແຈກຢາຍ',
        post_box: 'ກ່ອງໄປສະນີ',
        central: 'ສູນແກ້ໄຂ',
      },
      // New optimization data
      lastUpdated: null,
      filterDebounceTimer: null,
      abortController: null,
      sortField: null,
      sortDirection: 'asc',
      selectedBranches: [],
      isBatchProcessing: false,
      selectAllPages: false,
      showConfirmDialog: false,
      confirmMessage: '',
      confirmDanger: false,
      pendingAction: null,
      walletServiceAvailable: true,
      isExporting: false,
      exportPollTimer: null,
      exportPollCount: 0,
      // Monotonic token: every startExportPolling (and beforeDestroy) bumps it, so a
      // continuation from an older session bails instead of touching the new one.
      exportPollSession: 0,
      exportDestroyed: false,
    };
  },
  computed: {
    formatLastUpdated() {
      if (!this.lastUpdated) return '';
      return new Date(this.lastUpdated).toLocaleTimeString('lo-LA', {
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    sortedBranches() {
      if (!this.sortField) return this.branches;

      return [...this.branches].sort((a, b) => {
        let aVal, bVal;

        switch (this.sortField) {
          case 'prefix':
            aVal = a.prefix || '';
            bVal = b.prefix || '';
            break;
          case 'name':
            aVal = a.name || '';
            bVal = b.name || '';
            break;
          case 'freight_balance':
            aVal = a.wallets?.freight_wallet?.balance ?? 0;
            bVal = b.wallets?.freight_wallet?.balance ?? 0;
            break;
          case 'cod_balance':
            aVal = a.wallets?.cod_wallet?.balance ?? 0;
            bVal = b.wallets?.cod_wallet?.balance ?? 0;
            break;
          default:
            return 0;
        }

        if (typeof aVal === 'string') {
          const compare = aVal.localeCompare(bVal, 'lo');
          return this.sortDirection === 'asc' ? compare : -compare;
        }

        return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      });
    },
    isAllSelected() {
      return this.branches.length > 0 && this.selectedBranches.length === this.branches.length;
    },
    isPartiallySelected() {
      return this.selectedBranches.length > 0 && this.selectedBranches.length < this.branches.length;
    },
  },
  created() {
    this.fetchBranches();
  },
  beforeDestroy() {
    // Cleanup: cancel pending requests and timers
    this.cancelPendingRequests();
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }
    this.exportDestroyed = true;
    this.stopExportPolling(); // also invalidates any in-flight poll continuation
  },
  methods: {
    // Row number calculation with pagination
    getRowNumber(index) {
      return (this.currentPage - 1) * this.perPage + index + 1;
    },

    // Filter change handler (debounced to prevent rapid re-fetches on dropdown changes)
    onFilterChange() {
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer);
      }
      this.filterDebounceTimer = setTimeout(() => {
        this.currentPage = 1;
        this.fetchBranches();
      }, 300);
    },

    // Cancel pending requests
    cancelPendingRequests() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
    },

    // Refresh data
    refreshData() {
      this.fetchBranches();
    },

    // Export the branch-wallets dump (async export job on the Go service):
    // create → poll → auto-download. On-screen filters are NOT applied, with
    // ONE exception: while ຖືກລັອກ (blocked) is selected the job is a
    // WALLETS_BLOCKED export (+ wallet-type narrowing) — Go emits only the
    // blocked wallets with their ຕ້ອງເຕີມ amounts, mirroring the screen. A
    // distinct job type (not a filter) so a Go binary that predates it FAILS
    // the job instead of silently returning the full dump.
    async exportWallets() {
      if (this.isExporting) return;
      this.isExporting = true;
      try {
        const blocked = this.balanceFilter === 'blocked';
        const filters = {};
        if (blocked && this.walletTypeFilter) {
          filters.wallet_type = this.walletTypeFilter;
        }
        const response = await walletService.createExportJob(this.$axios, blocked ? 'WALLETS_BLOCKED' : 'WALLETS', filters);
        // Create may resolve after navigation — never install a poller on a destroyed component.
        if (this.exportDestroyed) return;
        // Save-as fallback mirroring the server's download name — used only
        // when Content-Disposition is unreadable (old Go / stripping proxy).
        const today = new Date().toISOString().split('T')[0];
        const downloadName = `${blocked ? 'ກະເປົ໋າຖືກລັອກ' : 'ຍອດກະເປົ໋າ'}_${today}.xlsx`;
        this.startExportPolling(response.data.id, downloadName);
      } catch (error) {
        this.isExporting = false;
        if (this.exportDestroyed) return;
        this.$notification.OpenNotification_Error('top-right', 'danger', 4000, formatErrorMessage(error, 'ບໍ່ສາມາດສ້າງການສົ່ງອອກ'));
      }
    },

    startExportPolling(jobId, downloadName) {
      this.stopExportPolling();
      this.exportPollCount = 0;
      const session = ++this.exportPollSession;

      const pollFn = async () => {
        if (session !== this.exportPollSession) return;
        // Cap checked at tick ENTRY, before any request, so the ~2min bound holds
        // even when status requests hang or queue up.
        if (this.exportPollCount >= MAX_EXPORT_POLLS) {
          this.stopExportPolling();
          this.isExporting = false;
          this.$notification.OpenNotification_Error('top-right', 'warning', 5000, 'ການສົ່ງອອກໃຊ້ເວລາດົນຜິດປົກກະຕິ — ກະລຸນາລອງໃໝ່ພາຍຫຼັງ');
          return;
        }
        this.exportPollCount += 1;

        try {
          const response = await walletService.getExportJob(this.$axios, jobId);
          // A tick that was awaiting while its session ended (terminal state handled,
          // new export started, or component destroyed) must not act on the result —
          // that would double-download or hijack the newer session's timer.
          if (session !== this.exportPollSession) return;
          const job = response.data;

          if (job.status === 'COMPLETED') {
            this.stopExportPolling();
            try {
              await walletService.downloadExport(this.$axios, jobId, downloadName);
            } catch (error) {
              this.$notification.OpenNotification_Error('top-right', 'danger', 4000, formatErrorMessage(error, 'ບໍ່ສາມາດໂຫລດໄຟລ໌'));
            } finally {
              this.isExporting = false;
            }
            return;
          }
          if (job.status === 'FAILED') {
            this.stopExportPolling();
            this.isExporting = false;
            this.$notification.OpenNotification_Error('top-right', 'danger', 5000, job.error_message || 'ການສົ່ງອອກລົ້ມເຫລວ');
            return;
          }
        } catch (error) {
          if (session !== this.exportPollSession) return;
          // Transient poll error — keep polling until the entry-time bound above.
          console.error('Export poll error:', error);
        }
      };

      pollFn();
      this.exportPollTimer = setInterval(pollFn, POLLING_INTERVAL);
    },

    stopExportPolling() {
      // Bump the session on every stop so a same-session tick still awaiting its
      // status response bails on resume (it would otherwise re-handle a terminal
      // state that this stop already handled — double download).
      this.exportPollSession += 1;
      if (this.exportPollTimer) {
        clearInterval(this.exportPollTimer);
        this.exportPollTimer = null;
      }
    },

    async fetchBranches() {
      // Cancel any pending request
      this.cancelPendingRequests();

      this.isLoading = true;
      this.abortController = new AbortController();

      // Drop any prior blocked-set strip as soon as we leave the blocked filter, so
      // it never lingers over a different filter's results (a blocked→blocked refresh
      // keeps it until the response repopulates it, avoiding a flicker).
      if (this.balanceFilter !== 'blocked') {
        this.healthSummary = null;
      }

      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
        };
        if (this.searchQuery) params.search = this.searchQuery;
        if (this.walletFilter !== 'all') {
          params.wallet_enabled = this.walletFilter === 'enabled';
        }
        if (this.typeFilter) params.type = this.typeFilter;
        // Balance-health filter is applied SERVER-SIDE now: Laravel delegates the
        // match to Go's overview endpoint across ALL branches (fixes the old
        // client-side per-page filter). The 'low' option maps to the 'warning' bucket;
        // 'blocked' passes through as-is (deadline-locked wallets, not a balance rank).
        // An optional wallet_type narrows the match to one wallet (e.g. negative COD);
        // it only has meaning alongside a health filter.
        if (this.balanceFilter !== 'all') {
          params.health = this.balanceFilter === 'low' ? 'warning' : this.balanceFilter;
          if (this.walletTypeFilter) {
            params.wallet_type = this.walletTypeFilter;
          }
        }

        const response = await this.$axios.get('wallet/branches', {
          params,
          signal: this.abortController.signal,
          timeout: REQUEST_TIMEOUT,
        });

        this.allBranches = response.data.data || [];
        this.branches = this.allBranches;
        this.totalBranches = response.data.total || 0;
        this.totalPages = response.data.last_page || 1;
        this.walletServiceAvailable = response.data.wallet_service_available !== false;
        this.lastUpdated = Date.now();

        // Surface when the requested health filter could not be applied (Go down,
        // or the matching set was too large to apply reliably), via both an inline
        // banner and a toast, so the operator never mistakes the (possibly empty)
        // result for a complete answer.
        this.healthFilterError = response.data.health_filter_error === true;
        if (this.healthFilterError) {
          this.$notification.OpenNotification_Error('top-right', 'warning', 4000, 'ບໍ່ສາມາດກັ່ນຕອງຕາມສະຖານະຍອດເງິນໄດ້ໃນຂະນະນີ້');
        }
        // Finance summary rides only the blocked filter's response (absent otherwise).
        this.healthSummary = response.data.health_summary || null;
      } catch (error) {
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
          return;
        }
        // A failed fetch must not leave a stale blocked-set strip showing over a
        // result the response did not actually provide.
        this.healthSummary = null;
        console.error('Fetch branches error:', error.response?.data || error);
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, formatErrorMessage(error, 'ບໍ່ສາມາດໂຫລດຂໍ້ມູນໄດ້'));
      } finally {
        this.isLoading = false;
        this.abortController = null;
      }
    },

    // Sorting
    setSort(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
    },

    getSortIcon(field) {
      if (this.sortField !== field) return 'fal fa-sort';
      return this.sortDirection === 'asc' ? 'fal fa-sort-up' : 'fal fa-sort-down';
    },

    // Balance class
    getBalanceClass(wallet) {
      if (!wallet) return '';
      if (wallet.balance < 0) return 'negative';
      if (this.isBalanceLow(wallet)) return 'warning';
      return '';
    },

    formatBranchType(type) {
      return this.branchTypes[type] || type;
    },

    formatCurrency(amount) {
      return formatCurrency(amount);
    },

    formatLimit(limit) {
      if (!limit || limit >= 0) return 'ບໍ່ກຳນົດ';
      return this.formatCurrency(limit);
    },

    hasNegativeLimit(wallet) {
      return wallet && wallet.minimum_negative_limit && wallet.minimum_negative_limit < 0;
    },

    isBalanceLow(wallet) {
      if (!wallet) return false;
      const minBalance = wallet.minimum_balance || 0;
      if (minBalance <= 0) return false;
      return wallet.balance < minBalance;
    },

    formatNumberInput,
    parseNumberInput,

    parsePositiveNumber(value) {
      if (!value) return 0;
      const cleaned = String(value).replace(/[^\d]/g, '');
      return parseInt(cleaned, 10) || 0;
    },

    // Confirmation dialog
    confirmToggleWallet(branch, event) {
      if (branch.wallet_enabled) {
        // Revert checkbox immediately — only toggle after confirmation
        event.target.checked = true;
        // Disabling - show confirmation
        this.confirmMessage = `ທ່ານຕ້ອງການປິດກະເປົ໋າຂອງສາຂາ "${branch.name}" ແທ້ບໍ?`;
        this.confirmDanger = true;
        this.pendingAction = () => this.toggleWalletStatus(branch);
        this.showConfirmDialog = true;
      } else {
        // Enabling - no confirmation needed
        this.toggleWalletStatus(branch);
      }
    },

    executeConfirmedAction() {
      this.showConfirmDialog = false;
      if (this.pendingAction) {
        this.pendingAction();
        this.pendingAction = null;
      }
    },

    async toggleWalletStatus(branch) {
      this.togglingBranchId = branch.id;
      const originalStatus = branch.wallet_enabled;
      const newStatus = !originalStatus;

      // Optimistic update
      branch.wallet_enabled = newStatus;

      try {
        // Call Laravel which handles wallet service integration
        await walletService.setWalletEnabled(this.$axios, branch.id, newStatus);

        this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);

        // Refresh to get updated wallet data
        this.fetchBranches();
      } catch (error) {
        branch.wallet_enabled = originalStatus;
        console.error('Toggle wallet error:', error.response?.data || error);
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, formatErrorMessage(error, 'ບໍ່ສາມາດປ່ຽນສະຖານະໄດ້'));

        // Refresh list so service-down banner appears if Go is unreachable
        this.fetchBranches();
      } finally {
        this.togglingBranchId = null;
      }
    },

    // Batch selection
    isSelected(branchId) {
      return this.selectedBranches.includes(branchId);
    },

    toggleSelect(branchId) {
      const index = this.selectedBranches.indexOf(branchId);
      if (index === -1) {
        this.selectedBranches.push(branchId);
      } else {
        this.selectedBranches.splice(index, 1);
      }
    },

    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedBranches = [];
        this.selectAllPages = false;
      } else {
        this.selectedBranches = this.branches.map(b => b.id);
      }
    },

    async selectAllAcrossPages() {
      this.selectAllPages = true;
      // Fetch all branch IDs matching current filters from the lightweight branch-options endpoint
      // (id-only, NO Go balance fan-out) — selecting all branches must not issue one Go balance call
      // per branch. branch-options has a high per_page cap to return the full filtered set in one page.
      try {
        // include_closed: this screen lists CLOSED branches (they can still owe the company), so
        // "select all" must cover the same set the table shows. The flag is opt-in precisely so the
        // other BranchSelect consumers (Monitor, Pending-Topups, DeliveryProof) stay active-only.
        const params = { per_page: 1000, include_closed: 1 };
        if (this.searchQuery) params.search = this.searchQuery;
        if (this.walletFilter !== 'all') {
          params.wallet_enabled = this.walletFilter === 'enabled';
        }
        if (this.typeFilter) params.type = this.typeFilter;

        const response = await this.$axios.get('wallet/branch-options', { params });
        const allIds = (response.data.data || []).map(b => b.id);
        this.selectedBranches = allIds;
      } catch {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ບໍ່ສາມາດໂຫລດລາຍການສາຂາທັງໝົດໄດ້');
        this.selectAllPages = false;
      }
    },

    clearSelection() {
      this.selectedBranches = [];
      this.selectAllPages = false;
    },

    async batchEnableWallets() {
      const count = this.selectAllPages ? this.totalBranches : this.selectedBranches.length;
      this.confirmMessage = `ທ່ານຕ້ອງການເປີດກະເປົ໋າໃຫ້ ${count} ສາຂາ ແທ້ບໍ?`;
      this.confirmDanger = false;
      this.pendingAction = () => this.executeBatchUpdate(true);
      this.showConfirmDialog = true;
    },

    async batchDisableWallets() {
      const count = this.selectAllPages ? this.totalBranches : this.selectedBranches.length;
      this.confirmMessage = `ທ່ານຕ້ອງການປິດກະເປົ໋າໃຫ້ ${count} ສາຂາ ແທ້ບໍ?`;
      this.confirmDanger = true;
      this.pendingAction = () => this.executeBatchUpdate(false);
      this.showConfirmDialog = true;
    },

    async executeBatchUpdate(enabled) {
      this.isBatchProcessing = true;

      try {
        const result = await walletService.batchSetWalletEnabled(
          this.$axios,
          [...this.selectedBranches],
          enabled
        );

        // Update local state for succeeded branches
        for (const branchId of (result.succeeded || [])) {
          const branch = this.allBranches.find(b => b.id === branchId);
          if (branch) branch.wallet_enabled = enabled;
        }

        const failCount = (result.failed || []).length;
        if (failCount === 0) {
          this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
        } else {
          this.$notification.OpenNotification_Error('top-right', 'warning', 4000,
            result.message || `ລົ້ມເຫລວ ${failCount} ສາຂາ`);
        }
      } catch (error) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 4000,
          formatErrorMessage(error, 'ບໍ່ສາມາດດຳເນີນການໄດ້'));
      }

      this.isBatchProcessing = false;
      this.selectedBranches = [];
      this.selectAllPages = false;
      this.fetchBranches();
    },

    openTopUpDialog(branch) {
      this.selectedBranch = branch;
      this.showTopUpDialog = true;
    },

    openTransferDialog(branch) {
      this.selectedBranch = branch;
      this.showTransferDialog = true;
    },

    openTransactionHistory(branch) {
      this.selectedBranch = branch;
      this.showTransactionHistory = true;
    },

    openLimitDialog(branch, walletType) {
      this.selectedBranch = branch;
      this.selectedWalletType = walletType;

      if (walletType === 'FREIGHT') {
        this.selectedWallet = branch.wallets?.freight_wallet;
      } else {
        this.selectedWallet = branch.wallets?.cod_wallet;
      }

      if (this.selectedWallet) {
        this.limitForm = {
          minimum_balance: this.selectedWallet.minimum_balance || 0,
          minimum_negative_limit: this.selectedWallet.minimum_negative_limit || 0,
        };
      } else {
        this.limitForm = {
          minimum_balance: 0,
          minimum_negative_limit: 0,
        };
      }

      this.showLimitDialog = true;
    },

    getCurrentBalance() {
      return this.selectedWallet?.balance ?? 0;
    },

    calculateAvailableBalance() {
      const balance = this.getCurrentBalance();
      const negativeLimit = this.limitForm.minimum_negative_limit || 0;
      return balance - negativeLimit;
    },

    async saveLimits() {
      if (!this.selectedWallet) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ບໍ່ພົບຂໍ້ມູນກະເປົ໋າ');
        return;
      }

      const debtLimit = Math.abs(this.limitForm.minimum_negative_limit);

      if (debtLimit > 100000000) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ວົງເງິນໜີ້ສູງສຸດບໍ່ເກີນ 100,000,000');
        return;
      }

      if (this.limitForm.minimum_balance < 0) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ຍອດເງິນຂັ້ນຕ່ຳຕ້ອງເປັນຄ່າບວກ ຫຼື 0');
        return;
      }

      if (this.limitForm.minimum_balance > 100000000) {
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ຍອດເງິນຂັ້ນຕ່ຳຕ້ອງບໍ່ເກີນ 100,000,000');
        return;
      }

      if (this.limitForm.minimum_balance > 0 && debtLimit > 0) {
        if (this.limitForm.minimum_balance >= debtLimit) {
          this.$notification.OpenNotification_Error('top-right', 'danger', 3000, 'ຍອດເງິນຂັ້ນຕ່ຳຕ້ອງໜ້ອຍກວ່າວົງເງິນໜີ້ສູງສຸດ (ເຊັ່ນ: ຂັ້ນຕ່ຳ 50,000 ກັບວົງເງິນໜີ້ 100,000)');
          return;
        }
      }

      this.isSavingLimits = true;
      try {
        await walletService.updateLimits(
          this.$axios,
          this.selectedWallet.id,
          this.limitForm.minimum_balance,
          this.limitForm.minimum_negative_limit
        );

        this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
        this.showLimitDialog = false;
        this.fetchBranches();
      } catch (error) {
        console.error('Save limits error:', error.response?.data || error);
        this.$notification.OpenNotification_Error('top-right', 'danger', 3000, formatErrorMessage(error, 'ບໍ່ສາມາດບັນທຶກໄດ້'));
      } finally {
        this.isSavingLimits = false;
      }
    },

    onTopUpSuccess(topUpResult) {
      this.showTopUpDialog = false;

      // Update the local wallet balance from the Go service response
      // (fetchBranches returns stale data from Laravel, so we use the balance_after directly)
      if (topUpResult?.branchId && topUpResult?.balanceAfter != null) {
        const branch = this.allBranches.find(b => b.id === topUpResult.branchId);
        if (branch && branch.wallets) {
          const walletKey = topUpResult.walletType === 'FREIGHT' ? 'freight_wallet' : 'cod_wallet';
          if (branch.wallets[walletKey]) {
            this.$set(branch.wallets[walletKey], 'balance', topUpResult.balanceAfter);
          }
        }
      }
    },

    onTransferSuccess(transferResult) {
      this.showTransferDialog = false;

      // A transfer moves money between this branch's two wallets — both balances change. Update
      // each locally from the Go response (same staleness rationale as onTopUpSuccess: fetchBranches
      // returns Laravel-side data that lags a Go money move). Net branch liability is unchanged.
      if (!transferResult?.branchId) return;
      const branch = this.allBranches.find(b => b.id === transferResult.branchId);
      if (!branch || !branch.wallets) return;

      const walletKey = type => (type === 'FREIGHT' ? 'freight_wallet' : 'cod_wallet');
      if (transferResult.sourceBalanceAfter != null) {
        const srcKey = walletKey(transferResult.sourceWalletType);
        if (branch.wallets[srcKey]) {
          this.$set(branch.wallets[srcKey], 'balance', transferResult.sourceBalanceAfter);
        }
      }
      if (transferResult.destBalanceAfter != null) {
        const destKey = walletKey(transferResult.destWalletType);
        if (branch.wallets[destKey]) {
          this.$set(branch.wallets[destKey], 'balance', transferResult.destBalanceAfter);
        }
      }
    },

    onMigrateSuccess(migrateResult) {
      this.showMigrateDialog = false;
      // A migration moves BOTH wallet types of the source to the destination. Update each visible
      // branch locally from the Go response (a skipped type is null — nothing moved). Deliberately NO
      // fetchBranches() here: like onTransferSuccess, the Laravel list lags a Go money move and would
      // overwrite the fresh balances with stale ones.
      if (!migrateResult) return;
      const src = this.allBranches.find(b => b.id === migrateResult.sourceBranchId);
      const dst = this.allBranches.find(b => b.id === migrateResult.destBranchId);
      const walletKey = type => (type === 'FREIGHT' ? 'freight_wallet' : 'cod_wallet');
      for (const r of [migrateResult.freight, migrateResult.cod]) {
        if (!r) continue;
        const key = walletKey(r.wallet_type);
        if (src && src.wallets && src.wallets[key] && r.source_balance_after != null) {
          this.$set(src.wallets[key], 'balance', r.source_balance_after);
        }
        if (dst && dst.wallets && dst.wallets[key] && r.dest_balance_after != null) {
          this.$set(dst.wallets[key], 'balance', r.dest_balance_after);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  margin-top: 20px;
}

.box-title {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    font-size: 18px;
    font-weight: 600;

    i {
      margin-right: 8px;
      color: #28a745;
    }
  }

  .title-actions {
    display: flex;
    align-items: center;
    gap: 12px;

    // Distinct from the app-blue primary actions: a migration closes a branch.
    .button.migrate-open-btn {
      background: #d9534f;
      color: #fff;
      &:hover { background: #c9302c; }
      &[disabled] { background: #e9a5a3; }
    }

    .last-updated {
      font-size: 12px;
      color: #666;

      i {
        margin-right: 4px;
      }
    }
  }
}

.countTotal {
  color: #666;
  font-weight: normal;
}

// Batch actions
.batch-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #e3f2fd;
  border: 1px solid #90caf9;
  margin-bottom: 16px;

  .selected-count {
    font-weight: 500;
    color: #1565c0;
  }

  .batch-buttons {
    display: flex;
    gap: 8px;
  }
}

.batch-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.selected-row {
  background-color: #e3f2fd !important;
}

// Sortable headers
.sortable {
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #f5f5f5;
  }

  i {
    margin-left: 4px;
    font-size: 12px;
    color: #999;
  }
}

// Skeleton loading
.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-checkbox {
  width: 18px;
  height: 18px;
}

.skeleton-text {
  width: 80px;
  height: 16px;
}

.skeleton-text-sm {
  width: 50px;
  height: 16px;
}

.skeleton-toggle {
  width: 60px;
  height: 24px;
  border-radius: 12px;
}

.skeleton-actions {
  width: 60px;
  height: 24px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-active {
  background-color: #d4edda;
  color: #155724;
}

.status-inactive {
  background-color: #f8d7da;
  color: #721c24;
}

.closed-branch-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 10px;
  background: #ffeceb;
  color: #b02a20;
  border: 1px solid #f3c2be;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  vertical-align: middle;
}

.switch-toggle {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 8px;

  input {
    display: none;
  }

  .slider {
    position: relative;
    width: 44px;
    height: 24px;
    background-color: #ccc;
    border-radius: 24px;
    transition: background-color 0.3s;

    &::before {
      content: '';
      position: absolute;
      width: 18px;
      height: 18px;
      background-color: white;
      border-radius: 50%;
      top: 3px;
      left: 3px;
      transition: transform 0.3s;
    }
  }

  input:checked + .slider {
    background-color: #28a745;

    &::before {
      transform: translateX(20px);
    }
  }

  input:disabled + .slider {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .toggle-label {
    font-size: 12px;
    font-weight: 500;

    &.active {
      color: #28a745;
    }

    &.inactive {
      color: #6c757d;
    }
  }
}

.balance-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.balance-text {
  font-weight: 600;
  color: #28a745;

  &.warning {
    color: #ffc107;
  }

  &.negative {
    color: #dc3545;
  }
}

.warning-icon {
  color: #ffc107;
  font-size: 14px;
  animation: pulse 1.5s infinite;
}

.cod-blocked-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background-color: #fff3e0;
  color: #e65100;
  font-size: 11px;
  font-weight: 600;
}

/* Per-row "must top up to unblock" amount, rendered next to the lock chip. */
.unblock-amount {
  display: inline-block;
  margin-left: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #e65100;
  white-space: nowrap;
}

/* Blocked-set finance summary strip (blocked filter only). */
.blocked-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  margin-bottom: 12px;
  border-left: 4px solid #e65100;
  background-color: #fff8f2;

  .blocked-summary-head {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: #e65100;
  }

  .blocked-summary-types {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  .blocked-summary-type {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;

    .type-label {
      font-weight: 600;
      color: #444;
    }

    .type-metric strong {
      color: #e65100;
    }

    .type-count {
      color: #999;
      font-size: 12px;
    }
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.text-muted {
  color: #999;
}

.limit-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.limit-text {
  font-size: 12px;
  font-weight: 500;

  &.negative {
    color: #dc3545;
  }

  &.not-set {
    color: #999;
    font-style: italic;
  }
}

.badge-warning {
  background-color: #ffc107 !important;
  color: #000 !important;
}

.not-found {
  text-align: center;
  padding: 40px;
  color: #999;
}

.card-tabs {
  ul li a {
    cursor: pointer;

    &:hover {
      color: #28a745;
    }
  }

  li.is-active a {
    border-bottom-color: #28a745;
    color: #28a745;
  }
}

// Dialog Styles
.dialog-title {
  font-size: 16px;
  font-weight: 600;

  i {
    margin-right: 8px;
    color: #007bff;
  }
}

.limit-dialog-content {
  padding: 10px 0;

  .info-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    .info-label {
      color: #666;
    }

    .info-value {
      font-weight: 500;
    }
  }

  hr {
    margin: 15px 0;
    border: none;
    border-top: 1px solid #eee;
  }

  .form-group {
    margin-bottom: 20px;

    .label {
      font-weight: 500;
      margin-bottom: 8px;
    }

    .help {
      font-size: 12px;
      color: #888;
      margin-top: 5px;
    }
  }

  .available-balance-preview {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .preview-label {
      font-weight: 500;
      color: #333;
    }

    .preview-value {
      font-size: 18px;
      font-weight: 700;
      color: #28a745;
    }
  }
}

.confirm-content {
  padding: 20px 0;
  text-align: center;

  p {
    font-size: 16px;
    color: #333;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
